import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useStore } from '../store/useStore';
import { getSheetPdfUrl } from '../utils/api';
import { hasNewerSheetVersion } from '../utils/versionSets';
import MarkupCanvas from './MarkupCanvas';
import MarkupPopup from './MarkupPopup';
import styles from './PdfViewer.module.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

// Supersample factor for the one-shot canvas render. Every zoom gesture after
// load is a pure CSS scale — no PDF re-rasterization. 3× keeps things crisp
// from ~0.3× to 3× visual zoom; past 3× you get mild pixel upscaling.
const RENDER_SCALE = 3;

export default function PdfViewer() {
  const {
    activeSheet, zoom, pan, setPan, setZoom, markups, updateMarkup, removeMarkup,
    sheets, versionSets,
  } = useStore();

  // Stale-version detection: if a newer version of this sheet number
  // exists elsewhere in the project (any VS with a later issuance_date,
  // ties broken by published_at), surface a red banner so the user knows
  // they're looking at history. Per-sheet rather than per-VS — a project
  // with multiple per-discipline VSes on the same date doesn't false-flag
  // sheets in the non-winning VSes.
  const isStale = !!(activeSheet && hasNewerSheetVersion(activeSheet, sheets));
  const staleVsName = isStale
    ? versionSets.find((v) => v.id === activeSheet.version_set_id)?.name
    : null;

  // Per-session banner dismiss: clicking the × hides the banner for the
  // current sheet only. Switching to another sheet (whether also stale or
  // not) resets the dismiss so the banner reappears, since opening a
  // different older drawing is a fresh viewing session that deserves the
  // warning. Tracked by sheet id so the dismissed state lives with the
  // sheet, not as ambient state that would persist across sessions.
  const [dismissedSheetId, setDismissedSheetId] = useState(null);
  const bannerVisible = isStale && dismissedSheetId !== activeSheet.id;

  const containerRef  = useRef(null);
  const canvasRef     = useRef(null);
  const pdfRef        = useRef(null);
  const renderTaskRef = useRef(null);

  const [pageSize,   setPageSize]   = useState({ width: 0, height: 0 });
  // renderZoom = the zoom level the canvas was rasterized at (= RENDER_SCALE).
  // Every subsequent zoom is just a CSS scale() transform — no PDF re-render.
  // At zoom < RENDER_SCALE we scale down a supersampled canvas (stays crisp);
  // at zoom > RENDER_SCALE we scale up raster pixels (mild softening).
  const [renderZoom, setRenderZoom] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  const [selectedMarkupId, setSelectedMarkupId] = useState(null);

  // Pan state (refs so mouse-move handlers don't need re-registration)
  const isPanning   = useRef(false);
  const panStart    = useRef({ x: 0, y: 0 });
  const panAtStart  = useRef({ x: 0, y: 0 });

  // Wheel-smoothing state (targets + rAF handle)
  const targetZoomRef = useRef(1);
  const targetPanRef  = useRef({ x: 0, y: 0 });
  const animRef       = useRef(null);

  // ── Load PDF when active sheet changes ────────────────────────────────────
  useEffect(() => {
    if (!activeSheet) return;
    // Kill any wheel-tween from the previous sheet before resetting pan/zoom
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
    setLoading(true);
    setError(null);
    setSelectedMarkupId(null);

    const url = getSheetPdfUrl(activeSheet.id);
    pdfjsLib.getDocument(url).promise
      .then((pdf) => {
        pdfRef.current = pdf;
        return pdf.getPage(1);
      })
      .then((page) => {
        const vp = page.getViewport({ scale: 1 });
        setPageSize({ width: vp.width, height: vp.height });

        // Open every sheet fit-to-window centered. Without this, sheets
        // opened a 100% zoom anchored at the top-left corner — basically
        // useless for a D/E-size drawing on a 1080p screen. The 0.95
        // factor leaves a small margin so the page edges aren't flush
        // with the container. Sets store + tween targets together so the
        // wheel-smoothing rAF doesn't fight the initial state.
        const c = containerRef.current?.getBoundingClientRect();
        if (c && c.width > 0 && c.height > 0) {
          const fitZoom = Math.min(c.width / vp.width, c.height / vp.height) * 0.95;
          const scaledW = vp.width  * fitZoom;
          const scaledH = vp.height * fitZoom;
          const centerPan = {
            x: (c.width  - scaledW) / 2,
            y: (c.height - scaledH) / 2,
          };
          setZoom(fitZoom);
          setPan(centerPan);
          targetZoomRef.current = fitZoom;
          targetPanRef.current  = centerPan;
        } else {
          // Fallback if the container isn't measured yet (unlikely on
          // sheet load — the layout has been mounted for a while).
          targetPanRef.current  = { x: 0, y: 0 };
          targetZoomRef.current = useStore.getState().zoom;
          setPan({ x: 0, y: 0 });
        }

        setLoading(false);
        // Render ONCE at the highest supported scale — iOS clamps to 1.5 to
        // stay under canvas-size limits; desktop stays at RENDER_SCALE (3).
        // cssScale = zoom / renderZoom keeps layout correct either way.
        const actual = renderPage(page, RENDER_SCALE);
        setRenderZoom(actual);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });

    return () => { if (renderTaskRef.current) renderTaskRef.current.cancel(); };
  }, [activeSheet]); // eslint-disable-line

  // ── Core PDF render ───────────────────────────────────────────────────────
  // Returns the actual rendered scale (may be lower than requested on mobile
  // due to iOS canvas-size limits). Caller stores it in renderZoom so the
  // cssScale bridge math stays correct.
  function renderPage(page, scale) {
    const canvas = canvasRef.current;
    if (!canvas) return scale;

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    const nativeVp = page.getViewport({ scale: 1 });
    let   renderScale = scale;
    let   dpr = window.devicePixelRatio || 1;

    // iOS Safari silently fails to allocate canvases beyond ~16 MP or
    // ~4096 px per edge. A D-size sheet rendered at scale=3 × DPR=2 is
    // 160 MP — way over. Detect mobile / Safari-on-iPad-claiming-Mac and
    // drop to a safer ceiling. This is the only reason PDFs won't render
    // on iPad; everything else works at full scale.
    const ua = navigator.userAgent || '';
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.maxTouchPoints > 1 && /Mac/.test(ua));
    if (isIOS) {
      renderScale = Math.min(renderScale, 1.5);
      dpr = 1;
    }

    // Belt-and-suspenders cap: regardless of platform, never exceed
    // ~14 MP total or ~4096 px per edge (covers non-iOS tablets too).
    let total = renderScale * dpr;
    const MAX_EDGE = 4096;
    const MAX_AREA = 14_000_000;
    const edge = Math.max(nativeVp.width, nativeVp.height) * total;
    if (edge > MAX_EDGE) total = MAX_EDGE / Math.max(nativeVp.width, nativeVp.height);
    const area = nativeVp.width * total * nativeVp.height * total;
    if (area > MAX_AREA) total = Math.sqrt(MAX_AREA / (nativeVp.width * nativeVp.height));

    const vp = page.getViewport({ scale: total });
    canvas.width  = vp.width;
    canvas.height = vp.height;
    // CSS size pretends the render was at `renderScale` (not `total`) so
    // the `cssScale = zoom / renderZoom` bridge math below matches the
    // intended display size. If we had to cap `total` below
    // `renderScale * dpr`, the only effect is a slightly-softer image —
    // layout and zoom math are unaffected.
    canvas.style.width  = `${nativeVp.width  * renderScale}px`;
    canvas.style.height = `${nativeVp.height * renderScale}px`;

    const task = page.render({ canvasContext: canvas.getContext('2d'), viewport: vp });
    renderTaskRef.current = task;
    task.promise.catch((e) => {
      if (e?.name !== 'RenderingCancelledException') console.error(e);
    });
    return renderScale;
  }

  // ── Smoothed zoom/pan animation ───────────────────────────────────────────
  // Wheel events set the target refs (declared up top); a rAF loop eases the
  // store toward them. Continuous input (trackpads) feels silky; discrete input
  // (notched wheels) tweens between clicks instead of snapping.
  function animateZoomPan() {
    const s  = useStore.getState();
    const z  = s.zoom, p  = s.pan;
    const tz = targetZoomRef.current, tp = targetPanRef.current;

    const closeEnough =
      Math.abs(tz - z) / Math.max(z, tz) < 0.001 &&
      Math.abs(tp.x - p.x) < 0.5 &&
      Math.abs(tp.y - p.y) < 0.5;

    if (closeEnough) {
      s.setZoom(tz);
      s.setPan({ x: tp.x, y: tp.y });
      animRef.current = null;
      return;
    }

    // Each frame pulls 35 % toward target → settles in ~5 frames (~80 ms).
    const K = 0.35;
    s.setZoom(z + (tz - z) * K);
    s.setPan({
      x: p.x + (tp.x - p.x) * K,
      y: p.y + (tp.y - p.y) * K,
    });
    animRef.current = requestAnimationFrame(animateZoomPan);
  }

  function cancelZoomAnim() {
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  }

  // ── Wheel: zoom-to-cursor with exponential factor + smoothing ─────────────
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    // Normalize deltaY across deltaModes (lines / pages → px-ish)
    let deltaY = e.deltaY;
    if (e.deltaMode === 1) deltaY *= 40;
    else if (e.deltaMode === 2) deltaY *= 400;

    // Exponential factor: one notch on a typical mouse (deltaY ≈ 100) ≈ 1/1.1.
    // Small trackpad deltas produce proportionally small zooms — fully continuous.
    const factor = Math.exp(-deltaY * 0.001);

    // Stack onto the pending target if animating, so fast scrolls accumulate
    // without waiting for the prior tween to finish.
    const s = useStore.getState();
    const baseZoom = animRef.current ? targetZoomRef.current : s.zoom;
    const basePan  = animRef.current ? targetPanRef.current  : s.pan;

    const newZoom      = Math.min(Math.max(baseZoom * factor, 0.1), 10);
    const actualFactor = newZoom / baseZoom;

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    // Cursor-lock the target state (invariant holds at settle, not mid-tween)
    targetZoomRef.current = newZoom;
    targetPanRef.current  = {
      x: cx - (cx - basePan.x) * actualFactor,
      y: cy - (cy - basePan.y) * actualFactor,
    };

    if (animRef.current == null) {
      animRef.current = requestAnimationFrame(animateZoomPan);
    }
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Cancel any running animation on unmount
  useEffect(() => () => cancelZoomAnim(), []);

  // ── Pointer-based pan + pinch-zoom ────────────────────────────────────────
  // Uses Pointer Events so mouse, pen, and touch all drive the same code path.
  //   • 1 pointer (touch with pan tool, or middle-click, or pen) → pan
  //   • 2 pointers (touch)                                       → pinch-zoom
  // Two-finger pinch on touch screens works regardless of active tool, matching
  // what users expect from every other PDF viewer on iPad.
  const { activeTool } = useStore();
  const activePointers = useRef(new Map()); // pointerId → { x, y }
  const pinchStart     = useRef(null);       // { dist, centerX, centerY, zoom, pan }

  function onPointerDown(e) {
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Two-finger gesture — takes precedence over any single-finger gesture.
    // We capture the starting midpoint + distance + zoom/pan, then in
    // onPointerMove we apply both the distance *ratio* (pinch-zoom) and the
    // midpoint *translation* (two-finger pan) in the same frame. This is
    // what lets the user pinch and pan simultaneously without letting go.
    if (activePointers.current.size === 2) {
      const pts = Array.from(activePointers.current.values());
      const dx  = pts[1].x - pts[0].x;
      const dy  = pts[1].y - pts[0].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const s = useStore.getState();
      cancelZoomAnim();
      pinchStart.current = {
        dist,
        centerX: (pts[0].x + pts[1].x) / 2,
        centerY: (pts[0].y + pts[1].y) / 2,
        zoom: s.zoom,
        pan: { ...s.pan },
      };
      isPanning.current = false; // cancel any in-progress pan
      e.preventDefault();
      return;
    }

    // Single-pointer pan: pan tool OR middle mouse OR any touch/pen in pan mode.
    // For drawing tools, MarkupCanvas claims the pointer via its own listener.
    const isMiddleMouse = e.button === 1;
    if (isMiddleMouse || activeTool === 'pan') {
      e.preventDefault();
      cancelZoomAnim();
      const s = useStore.getState();
      targetZoomRef.current = s.zoom;
      targetPanRef.current  = s.pan;
      isPanning.current  = true;
      panStart.current   = { x: e.clientX, y: e.clientY };
      panAtStart.current = { ...s.pan };
    }
  }

  function onPointerMove(e) {
    if (!activePointers.current.has(e.pointerId)) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Two-finger pinch+pan update — combined in one frame so the user can
    // pinch-zoom AND slide simultaneously (matches Photos / PDFs / Maps on
    // iPad). The math:
    //   • newZoom         = startZoom  * (curDist / startDist)
    //   • The content under the starting midpoint must stay under the
    //     *current* midpoint after the zoom. Solving for pan gives:
    //       pan = curMid - (startMid - startPan) * (newZoom / startZoom)
    //   • When fingers translate without pinching, curDist ≈ startDist so
    //     newZoom ≈ startZoom and the formula collapses to
    //       pan = curMid - (startMid - startPan) = startPan + (curMid - startMid)
    //     i.e. pure pan. When they pinch without translating, curMid ≈
    //     startMid and it collapses to the cursor-locked zoom formula.
    if (pinchStart.current && activePointers.current.size >= 2) {
      const pts = Array.from(activePointers.current.values()).slice(0, 2);
      const dx = pts[1].x - pts[0].x;
      const dy = pts[1].y - pts[0].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const factor  = dist / pinchStart.current.dist;
      const newZoom = Math.min(Math.max(pinchStart.current.zoom * factor, 0.1), 10);

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Current midpoint in container-local coords
      const curMidX = (pts[0].x + pts[1].x) / 2 - rect.left;
      const curMidY = (pts[0].y + pts[1].y) / 2 - rect.top;
      // Starting midpoint in container-local coords
      const startMidX = pinchStart.current.centerX - rect.left;
      const startMidY = pinchStart.current.centerY - rect.top;

      const actualFactor = newZoom / pinchStart.current.zoom;
      setZoom(newZoom);
      setPan({
        x: curMidX - (startMidX - pinchStart.current.pan.x) * actualFactor,
        y: curMidY - (startMidY - pinchStart.current.pan.y) * actualFactor,
      });
      return;
    }

    // Single-pointer pan update
    if (isPanning.current) {
      setPan({
        x: panAtStart.current.x + (e.clientX - panStart.current.x),
        y: panAtStart.current.y + (e.clientY - panStart.current.y),
      });
    }
  }

  function onPointerUp(e) {
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) pinchStart.current = null;
    if (activePointers.current.size === 0) isPanning.current = false;
  }

  const isMarkupTool = activeTool !== 'pan';
  // Crosshair overlay is enabled for precision tools: measurement + calibration.
  // Other drawing tools (arrow, rect, freehand, etc.) get the native crosshair
  // cursor only — no full-viewport lines.
  const isCrosshairTool =
    activeTool === 'calibrate' ||
    (typeof activeTool === 'string' && activeTool.startsWith('measure'));
  // The .page div is always sized to zoom — this drives pan hit-area and MarkupCanvas
  const canvasW = pageSize.width  * zoom;
  const canvasH = pageSize.height * zoom;
  // CSS scale bridges the gap between renderZoom and the current visual zoom
  const cssScale = zoom / renderZoom;

  return (
    <div
      ref={containerRef}
      className={styles.container}
      style={{
        cursor: activeTool === 'pan' ? (isPanning.current ? 'grabbing' : 'grab') : 'crosshair',
        // Disable browser-level touch gestures so our JS handles every
        // pan/pinch (iOS would otherwise try to pinch-zoom the page itself).
        touchAction: 'none',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      {loading && (
        <div className={styles.overlay}>
          <Spinner />
          <span>Loading sheet…</span>
        </div>
      )}
      {error && (
        <div className={styles.overlay}>
          <span className={styles.errorText}>Failed to load PDF: {error}</span>
        </div>
      )}
      {bannerVisible && (
        <div className={styles.staleBanner} role="alert">
          <span className={styles.staleIcon} aria-hidden>⚠</span>
          <span>
            You're viewing an older version of this drawing
            {staleVsName ? ` (${staleVsName})` : ''}. Switch the version-set
            filter in the sidebar to see the current sheets.
          </span>
          <button
            type="button"
            className={styles.staleDismiss}
            onClick={() => setDismissedSheetId(activeSheet.id)}
            title="Dismiss for this sheet"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      <div
        className={styles.page}
        style={{
          width:  canvasW,
          height: canvasH,
          transform: `translate(${pan.x}px, ${pan.y}px)`,
        }}
      >
        {/* Canvas is rasterized once at RENDER_SCALE; CSS scale() brings it to
            the current visual zoom. Down-scaling stays crisp (supersampled);
            up-scaling past RENDER_SCALE softens a bit. No PDF re-render ever. */}
        <canvas
          ref={canvasRef}
          className={styles.canvas}
          style={{
            transformOrigin: '0 0',
            transform: cssScale !== 1 ? `scale(${cssScale})` : undefined,
          }}
        />
        {pageSize.width > 0 && (
          <MarkupCanvas
            pageWidth={pageSize.width}
            pageHeight={pageSize.height}
            zoom={zoom}
            readOnly={!isMarkupTool}
            selectedId={selectedMarkupId}
            onSelectChange={setSelectedMarkupId}
          />
        )}
      </div>

      <CrosshairOverlay containerRef={containerRef} active={isCrosshairTool} />

      {selectedMarkupId && (() => {
        const sel = markups.find(m => m.id === selectedMarkupId);
        if (!sel) return null;
        return (
          <MarkupPopup
            key={selectedMarkupId}
            markup={sel}
            pan={pan}
            zoom={zoom}
            containerRef={containerRef}
            onClose={() => setSelectedMarkupId(null)}
            onUpdated={(updated) => updateMarkup(updated.id, updated)}
            onDeleted={(id) => { removeMarkup(id); setSelectedMarkupId(null); }}
          />
        );
      })()}
    </div>
  );
}

function Spinner() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.spinner}>
      <circle cx="12" cy="12" r="10" stroke="var(--border2)" strokeWidth="2"/>
      <path d="M12 2a10 10 0 0110 10" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

// Isolated cursor-position state so full-viewer re-renders don't fire on every
// mousemove. Tracks cursor in container-local coordinates; renders one 1px
// horizontal line + one 1px vertical line, both pointerEvents:none so they
// don't swallow clicks.
function CrosshairOverlay({ containerRef, active }) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!active) { setPos(null); return; }
    const el = containerRef.current;
    if (!el) return;
    function onMove(e) {
      const rect = el.getBoundingClientRect();
      setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
    function onLeave() { setPos(null); }
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [active, containerRef]);

  if (!active || !pos) return null;
  return (
    <>
      <div className={styles.crosshairHorizontal} style={{ top: pos.y }} />
      <div className={styles.crosshairVertical}   style={{ left: pos.x }} />
    </>
  );
}
